import { Button, Group, Progress, Text } from '@mantine/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

import { ContentWrap } from './ContentWrap';

interface PaymentInfo {
  order: number;
  created_at?: string;
  end_at?: string;
  used_tokens: number;
  max_tokens: number;
  used_questions: number;
  max_questions: number;
  type?: string;
  canceled_at?: string;
}

interface AccountProps {
  paymentInfo?: PaymentInfo;
}

export const Account = ({ paymentInfo }: AccountProps) => {
  const { t } = useTranslation();

  const hasSubscription = paymentInfo?.order !== 0;

  const format = (time?: string) => {
    if (!time) return '';
    return dayjs(time).tz('Asia/Tokyo').format('YYYY-MM-DD');
  };

  const upgrade = () => {
    window.open(`${import.meta.env.VITE_BASE_HOST}/pricing`, '_blank');
  };

  const usedTokens = useMemo(() => {
    if (!paymentInfo) return 0;
    return Math.round((paymentInfo.used_tokens / paymentInfo.max_tokens) * 100);
  }, [paymentInfo]);

  const usedQuestions = useMemo(() => {
    if (!paymentInfo) return 0;
    return Math.round((paymentInfo.used_questions / paymentInfo.max_questions) * 100);
  }, [paymentInfo]);

  const Usage = ({
    max = 0,
    title,
    used = 0,
    value,
  }: {
    max?: number;
    title: string;
    used?: number;
    value: number;
  }) => {
    return (
      <>
        <div className="flex items-center mt-8 mb-2">
          <Text fz="sm" fw={300} className="shrink-0">
            {title}：
          </Text>
        </div>
        <Group gap={0}>
          <Progress
            value={value}
            className="mb-2 sm:mb-0 sm:mr-2 w-full sm:w-3/4"
            radius="xl"
            size="lg"
            color="violet.5"
          />
          <Text fz="sm" color="gray.6" fw={300}>
            {used} / {max}
          </Text>
        </Group>
      </>
    );
  };

  return (
    <ContentWrap className="mt-5 p-5 w-full sm:w-3/4">
      <Group justify="space-between" className="mb-4">
        <Text color="gray.8" fw={500}>
          {t(`pricing.plan_${paymentInfo?.order || 0}.title`)}
        </Text>
        <Text color="gray.6" fw={400} fz="sm">
          {hasSubscription ? `${format(paymentInfo?.created_at)} ~ ${format(paymentInfo?.end_at)}` : ''}
        </Text>
      </Group>
      <Usage
        title={t('pricing.current_subscription.used_tokens')}
        used={paymentInfo?.used_tokens}
        max={paymentInfo?.max_tokens}
        value={usedTokens}
      />
      <Usage
        title={t('pricing.current_subscription.used_msgs')}
        used={paymentInfo?.used_questions}
        max={paymentInfo?.max_questions}
        value={usedQuestions}
      />

      {hasSubscription ? (
        <></>
      ) : (
        <Group className="mt-8">
          <Button onClick={upgrade}>{t('pricing.upgrade_button')}</Button>
        </Group>
      )}
    </ContentWrap>
  );
};
